import { BasePlugin } from '../../core/BasePlugin';
import { PluginMetadata, PluginCapabilities, MapInteractionEvent, MapDragEvent, MapHoverEvent, PluginCategory } from '../../types/PluginInterfaces';
import React from 'react';
import HomeIcon from '@mui/icons-material/Home';
import { RoomOutlineService } from './services/RoomOutlineService';
import { VirtualRoom } from './services/VirtualRoomStorage';
import { RoomBorderRenderer } from './RoomBorderRenderer';
import { VirtualRoomPropertiesPanel } from './components/VirtualRoomPropertiesPanel';
import { databaseBridge } from './services/DatabaseBridge';
import { virtualRoomService } from '../../../../../services/back4app/VirtualRoomService';
import { tileSyncManager, MAP_EDITOR_FLUSH_TIMEOUT_MS } from './services/TileSyncManager';
import { overlayUnsavedTiles } from './services/overlayUnsavedTiles';
import { currentMapId, requireCurrentMapId } from '../../../../../utils/currentMapId';
import { validateRoomArrivalPlacement } from './spawnPointValidation';

/** A VR's spawn point being placed: the VR, and the tile once the admin clicks the map. */
export interface SpawnPointDraft {
  tile: { x: number; y: number } | null;
  virtualRoomId: string;
}

export interface SpawnPointConfirmResult {
  success: boolean;
  reason?: string;
  warning?: string;
}

/**
 * The tile sync manager is one object for every editor session, and a closed editor's dispose
 * outlives its React cleanup (the provider doesn't await it). The newest session owns the manager:
 * an older dispose that wakes up later must not report or clear what the newer one painted.
 */
let latestSession = 0;

export class VirtualRoomPainterPlugin extends BasePlugin {
  // 0 until onInitialize: a plugin that never started owned nothing, as before.
  private session = 0;
  private isPainting = false;
  private lastPaintPosition: { x: number; y: number } | null = null;
  private roomOutlineService: RoomOutlineService;
  private currentRoom: VirtualRoom | null = null;
  private paintedTiles: Set<string> = new Set(); // Track tiles painted in current session
  // Single source of truth for which tool is active — a room-arrival "door" placement (SC-393)
  // is just a third mode alongside the existing tile add/delete modes, not a separate flag that
  // could independently read as active at the same time as add/delete.
  private editMode: 'add' | 'delete' | 'placePoint' = 'add'; // Track current edit mode
  private pendingSpawnPointDraft: SpawnPointDraft | null = null;
  
  // Modular border renderer
  private borderRenderer: RoomBorderRenderer;
  private borderTileIds: Map<string, number> = new Map(); // Map room colors to tile IDs (fallback)

  constructor() {
    const metadata: PluginMetadata = {
      id: 'virtual-room-painter',
      name: 'Virtual Room Painter',
      description: 'Advanced room painting tool with virtual area management',
      version: '1.0.0',
      author: 'MapEditor Team',
      category: PluginCategory.PAINTING,
      icon: React.createElement(HomeIcon),
      enabledByDefault: true,
      showInToolbar: true,
      toolbarOrder: 5
    };

    const capabilities: PluginCapabilities = {
      canPaintTiles: true,
      hasToolbarComponent: true,
      hasSidebarComponent: true,
      hasPropertiesPanel: true,
      requiresMapAccess: true,
      requiresPointerEvents: true
    };

    super(metadata, capabilities);
    
    // Initialize room outline service
    this.roomOutlineService = new RoomOutlineService();
    
    // Initialize modular border renderer
    this.borderRenderer = new RoomBorderRenderer({
      tileSize: 32,
      borderWidth: 2,
      borderOpacity: 0.8,
      depth: 1000
    });
    
    // Initialize border tile IDs for different colors
    this.initializeBorderTileIds();
  }

  /**
   * Initialize border tile IDs for different room colors
   */
  private initializeBorderTileIds(): void {
    // Map room colors to specific tile IDs for border tiles
    // These should be actual tile IDs from your tileset
    this.borderTileIds.set('#ff0000', 1001); // Red room border
    this.borderTileIds.set('#00ff00', 1002); // Green room border
    this.borderTileIds.set('#0000ff', 1003); // Blue room border
    this.borderTileIds.set('#ffff00', 1004); // Yellow room border
    this.borderTileIds.set('#ff00ff', 1005); // Magenta room border
    this.borderTileIds.set('#00ffff', 1006); // Cyan room border
    this.borderTileIds.set('#ff8800', 1007); // Orange room border
    this.borderTileIds.set('#8800ff', 1008); // Purple room border
    this.borderTileIds.set('#008800', 1009); // Dark green room border
    this.borderTileIds.set('#880000', 1010); // Dark red room border
  }

  /**
   * Plugin initialization
   */
  protected async onInitialize(): Promise<void> {
    // Before any await: a dispose of the previous editor that is still waiting must already see it.
    this.session = ++latestSession;
    console.log('[VirtualRoomPainterPlugin] Initializing Virtual Room Painter plugin');
    
    // The editor works on the editor room's map (SC-422).
    try {
      await databaseBridge.initialize(requireCurrentMapId());
      console.log('[VirtualRoomPainterPlugin] Database bridge initialized successfully');
    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Failed to initialize database bridge:', error);
      // Continue with local-only mode if database initialization fails
    }
    
    // Load the map's tiles from the database
    try {
      await this.loadTilesFromDatabase();
      console.log('[VirtualRoomPainterPlugin] Tiles loaded from database successfully');
    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Failed to load tiles from database:', error);
      // Continue without loaded tiles if database loading fails
    }
    
    // Register hotkey for activation
    this.registerHotkey('V', 'Activate Virtual Room Painter', async (event) => {
      if (!this.isActive) {
        await this.activate();
        return true;
      }
      return false;
    }, 80);
  }

  /**
   * Plugin activation - set up cursor and highlighting
   */
  protected async onActivate(): Promise<void> {
    console.log('[VirtualRoomPainterPlugin] Activating Virtual Room Painter plugin');

    // Request cursor control with high priority for painting tools
    this.requestCursorControl('crosshair', 85);

    // Enable cursor highlighting with virtual room specific style
    this.enableCursorHighlight();
    this.setHighlightStyle(0x8B4513, 0.9, 0.2); // Brown color for room painting

    // Set default brush settings for room painting
    this.setBrushSettings(1, 'square');

    // Set up border renderer with scene
    if (this.context.mapService) {
      const scene = (this.context.mapService as any).scene;
      if (scene) {
        this.borderRenderer.setScene(scene);
      }
    }

    // Ensure we're registered for pointer events
    if (this.context.mapPointers) {
      console.log('[VirtualRoomPainterPlugin] Setting as active handler');
      this.context.mapPointers.setActiveHandler(this.metadata.id);
    }

    // Emit activation event for UI components
    this.emit('pluginActivated', { pluginId: this.metadata.id });
  }

  /**
   * Plugin deactivation - clean up cursor and highlighting
   */
  protected async onDeactivate(): Promise<void> {
    console.log('[VirtualRoomPainterPlugin] Deactivating Virtual Room Painter plugin');

    // Release cursor control
    this.releaseCursorControl();

    // Disable cursor highlighting
    this.disableCursorHighlight();

    // Reset painting state
    this.isPainting = false;
    this.lastPaintPosition = null;
    this.pendingSpawnPointDraft = null;

    // Emit deactivation event for UI components
    this.emit('pluginDeactivated', { pluginId: this.metadata.id });
  }

  /**
   * Handle map drag events - continue painting during drag
   */
  public async onMapDrag(event: MapDragEvent): Promise<boolean> {
    console.log('[VirtualRoomPainterPlugin] Map drag event:', { x: event.tileX, y: event.tileY });
    
    if (!this.isActive || !this.currentRoom || !this.isPainting) {
      return false;
    }

    // Validate tile position using map bounds
    const mapData = this.context.getMapData();
    if (!mapData || event.tileX < 0 || event.tileY < 0 || 
        event.tileX >= mapData.width || event.tileY >= mapData.height) {
      return false;
    }

    // Check if we've moved to a new position
    if (this.lastPaintPosition && 
        this.lastPaintPosition.x === event.tileX && 
        this.lastPaintPosition.y === event.tileY) {
      return true; // Already processed this position
    }

    // Update last position
    this.lastPaintPosition = { x: event.tileX, y: event.tileY };

    // Handle based on edit mode
    if (this.editMode === 'add') {
      // Add tile to room
      await this.addTileToRoom(event.tileX, event.tileY);
    } else if (this.editMode === 'delete') {
      // Remove tile from room
      await this.removeTileFromRoom(event.tileX, event.tileY);
    }

    return true;
  }

  /**
   * Override handlePointerDown to add debugging
   */
  protected async handlePointerDown(event: MapInteractionEvent): Promise<boolean> {
    console.log('[VirtualRoomPainterPlugin] handlePointerDown called with event:', event);
    return super.handlePointerDown(event);
  }

  /**
   * Handle map click events - start painting
   */
  public async onMapClick(event: MapInteractionEvent): Promise<boolean> {
    console.log('[VirtualRoomPainterPlugin] Map click event:', { x: event.tileX, y: event.tileY });

    // placePoint mode doesn't require a currentRoom to be selected (an office-join or named
    // spawn point isn't tied to any room), so this check happens per-branch below instead of
    // gating the whole method the way the room tile brush modes do.
    if (!this.isActive) {
      console.log('[VirtualRoomPainterPlugin] Plugin not active');
      return false;
    }

    // Validate tile position using map bounds
    const mapData = this.context.getMapData();
    if (!mapData || event.tileX < 0 || event.tileY < 0 ||
        event.tileX >= mapData.width || event.tileY >= mapData.height) {
      console.warn('[VirtualRoomPainterPlugin] Invalid tile position:', { x: event.tileX, y: event.tileY });
      return false;
    }

    if (this.editMode === 'placePoint') {
      this.handlePlacePointClick(event.tileX, event.tileY);
      return true;
    }

    if (!this.currentRoom) {
      console.log('[VirtualRoomPainterPlugin] Plugin not active or no room selected');
      return false;
    }

    // Handle based on edit mode
    if (this.editMode === 'add') {
      // Start painting
      this.isPainting = true;
      this.lastPaintPosition = { x: event.tileX, y: event.tileY };

      // Add initial tile to room
      await this.addTileToRoom(event.tileX, event.tileY);
    } else if (this.editMode === 'delete') {
      // Start erasing
      this.isPainting = true;
      this.lastPaintPosition = { x: event.tileX, y: event.tileY };

      // Remove initial tile from room
      await this.removeTileFromRoom(event.tileX, event.tileY);
    }

    return true;
  }

  /**
   * Handle a click while in 'placePoint' mode: stages the clicked tile — using the same
   * event.tileX/event.tileY the 'add'/'delete' brush handlers already use — as the pending
   * spawn point's location. Does not persist anything yet; the caller (a properties panel or
   * inline confirm UI) must call confirmSpawnPoint() to save it.
   */
  private handlePlacePointClick(tileX: number, tileY: number): void {
    // A spawn point always belongs to a VR, so a click outside a placement does nothing.
    if (!this.pendingSpawnPointDraft) return;

    this.pendingSpawnPointDraft.tile = { x: tileX, y: tileY };
    console.log('[VirtualRoomPainterPlugin] Placed spawn point draft tile at:', { tileX, tileY });

    this.emit('spawnPointDraftTilePlaced', {
      tile: { x: tileX, y: tileY },
      draft: { ...this.pendingSpawnPointDraft }
    });
  }

  /**
   * Add a tile to the current room and update borders
   */
  private async addTileToRoom(tileX: number, tileY: number): Promise<void> {
    console.log('[VirtualRoomPainterPlugin] Adding tile to room:', { tileX, tileY });
    
    if (!this.currentRoom) {
      console.warn('[VirtualRoomPainterPlugin] No active room selected');
      return;
    }

    // Get brush settings for painting
    const brushSettings = this.context.brushSettingsManager?.getSettings() || { size: 1, shape: 'square' };
    console.log('[VirtualRoomPainterPlugin] Using brush settings:', brushSettings);

    // Get brush positions for the current brush settings
    const brushPositions = this.context.brushSettingsManager?.getBrushPositions(tileX, tileY) || [{ x: tileX, y: tileY }];
    console.log('[VirtualRoomPainterPlugin] Brush positions:', brushPositions);

    // Validate tile positions using map bounds
    const mapData = this.context.getMapData();
    if (!mapData) {
      console.warn('[VirtualRoomPainterPlugin] Map data not available');
      return;
    }

    let addedTiles = 0;

    // Add each brush position to the room
    for (const position of brushPositions) {
      if (position.x < 0 || position.y < 0 || 
          position.x >= mapData.width || position.y >= mapData.height) {
        console.warn('[VirtualRoomPainterPlugin] Invalid tile position:', { x: position.x, y: position.y });
        continue;
      }

      const coord = `${position.x},${position.y}`;
      
      // Check if tile is already painted
      if (this.paintedTiles.has(coord)) {
        console.log('[VirtualRoomPainterPlugin] Tile already painted:', coord);
        continue;
      }

      // Add tile to painted set
      this.paintedTiles.add(coord);
      addedTiles++;
      
      // Sync tile to database
      tileSyncManager.addTileChange(
        this.currentRoom.id,
        position.x,
        position.y,
        'room',
        'create'
      );
      
      console.log('[VirtualRoomPainterPlugin] Added tile to room:', coord);
    }

    if (addedTiles === 0) {
      console.log('[VirtualRoomPainterPlugin] No new tiles added');
      return;
    }

    // Update room outline data
    const tileArray = Array.from(this.paintedTiles).map(coord => {
      const [x, y] = coord.split(',').map(Number);
      return { x, y };
    });

    // Update room outline service
    const roomOutline = this.roomOutlineService.addRoomOutline(
      this.currentRoom.id,
      tileArray,
      this.currentRoom.color
    );

    // Update visual borders
    this.updateAdjacentTiles();

    // Check for adjacent rooms to merge
    this.checkAndMergeAdjacentRooms(this.currentRoom.id);

    console.log('[VirtualRoomPainterPlugin] Added', addedTiles, 'tiles. Room now has', this.paintedTiles.size, 'tiles total');
  }

  /**
   * Remove a tile from the current room and update borders
   */
  private async removeTileFromRoom(tileX: number, tileY: number): Promise<void> {
    console.log('[VirtualRoomPainterPlugin] Removing tile from room:', { tileX, tileY });
    
    if (!this.currentRoom) {
      console.warn('[VirtualRoomPainterPlugin] No active room selected');
      return;
    }

    // Get brush settings for erasing
    const brushSettings = this.context.brushSettingsManager?.getSettings() || { size: 1, shape: 'square' };
    console.log('[VirtualRoomPainterPlugin] Using brush settings for erasing:', brushSettings);

    // Get brush positions for the current brush settings
    const brushPositions = this.context.brushSettingsManager?.getBrushPositions(tileX, tileY) || [{ x: tileX, y: tileY }];
    console.log('[VirtualRoomPainterPlugin] Brush positions for erasing:', brushPositions);

    // Validate tile positions using map bounds
    const mapData = this.context.getMapData();
    if (!mapData) {
      console.warn('[VirtualRoomPainterPlugin] Map data not available');
      return;
    }

    let removedTiles = 0;

    // Remove each brush position from the room
    for (const position of brushPositions) {
      if (position.x < 0 || position.y < 0 || 
          position.x >= mapData.width || position.y >= mapData.height) {
        console.warn('[VirtualRoomPainterPlugin] Invalid tile position:', { x: position.x, y: position.y });
        continue;
      }

      const coord = `${position.x},${position.y}`;
      
      // Check if tile is actually painted
      if (!this.paintedTiles.has(coord)) {
        console.log('[VirtualRoomPainterPlugin] Tile not painted:', coord);
        continue;
      }

      // Remove tile from painted set
      this.paintedTiles.delete(coord);
      removedTiles++;
      
      // Sync tile deletion to database
      tileSyncManager.addTileChange(
        this.currentRoom.id,
        position.x,
        position.y,
        'room',
        'delete'
      );
      
      console.log('[VirtualRoomPainterPlugin] Removed tile from room:', coord);
    }

    if (removedTiles === 0) {
      console.log('[VirtualRoomPainterPlugin] No tiles removed');
      return;
    }

    // Update room outline data
    const tileArray = Array.from(this.paintedTiles).map(coord => {
      const [x, y] = coord.split(',').map(Number);
      return { x, y };
    });

    // Update room outline service
    const roomOutline = this.roomOutlineService.addRoomOutline(
      this.currentRoom.id,
      tileArray,
      this.currentRoom.color
    );

    // Update visual borders
    this.updateAdjacentTiles();

    console.log('[VirtualRoomPainterPlugin] Removed', removedTiles, 'tiles. Room now has', this.paintedTiles.size, 'tiles total');
  }

  /**
   * Execute the actual paint operation - creates room tiles with brush
   */
  private async executePaintOperation(tileX: number, tileY: number): Promise<void> {
    console.log('[VirtualRoomPainterPlugin] Executing brush paint operation at:', { tileX, tileY });
    
    if (!this.context.mapService || !this.context.brushSettingsManager) {
      console.warn('[VirtualRoomPainterPlugin] Required services not available');
      return;
    }

    if (!this.currentRoom) {
      console.warn('[VirtualRoomPainterPlugin] No active room selected');
      this.context.notificationService.show('Please select a room first', 'warning');
      return;
    }

    try {
      // Get brush positions for the current brush settings
      const brushPositions = this.context.brushSettingsManager.getBrushPositions(tileX, tileY);
      
      console.log('[VirtualRoomPainterPlugin] Painting room tiles at positions:', brushPositions);

      // Add each brush position to the room
      for (const position of brushPositions) {
        // Validate position before painting using map bounds
        const mapData = this.context.getMapData();
        if (mapData && position.x >= 0 && position.y >= 0 && 
            position.x < mapData.width && position.y < mapData.height) {
          
          // Add tile to room (this will handle painting and data storage)
          await this.addTileToRoom(position.x, position.y);
        }
      }

      // Show success notification
      this.context.notificationService.show(`Added ${brushPositions.length} tiles to ${this.currentRoom.name}`, 'success');

    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Error executing paint operation:', error);
      this.context.notificationService.show('Failed to paint room tiles', 'error');
    }
  }





  /**
   * Check if a coordinate is a valid room tile
   */
  private isValidRoomTile(coord: string): boolean {
    return this.paintedTiles.has(coord);
  }

  /**
   * Update adjacent tiles to create border effect
   */
  private updateAdjacentTiles(): void {
    console.log('[VirtualRoomPainterPlugin] Updating adjacent tiles for border effect');
    
    if (!this.currentRoom) {
      console.warn('[VirtualRoomPainterPlugin] No current room for border rendering');
      return;
    }
    
    // Get all painted tiles
    const tileArray = Array.from(this.paintedTiles).map(coord => {
      const [x, y] = coord.split(',').map(Number);
      return { x, y };
    });
    
    if (tileArray.length === 0) {
      console.log('[VirtualRoomPainterPlugin] No tiles to paint borders for');
      this.borderRenderer.clearBorders();
      return;
    }
    
    // Use the modular border renderer to paint the room border
    this.borderRenderer.paintRoomBorder(tileArray, this.currentRoom);
    
    console.log('[VirtualRoomPainterPlugin] Painted border for', tileArray.length, 'tiles using modular renderer');
  }

  /**
   * Get border tile ID for a room color
   */
  private getBorderTileId(color: string): number {
    // Get the tile ID for this color, or use a default
    const tileId = this.borderTileIds.get(color);
    if (tileId) {
      return tileId;
    }
    
    // If color not found, generate a tile ID based on color hash
    const colorHash = this.hashColor(color);
    return 1000 + (colorHash % 100); // Use 1000+ range for room tiles
  }

  /**
   * Check for adjacent rooms and merge them if needed
   */
  private checkAndMergeAdjacentRooms(currentRoomId: string): void {
    const allRooms = this.roomOutlineService.getAllRoomOutlines();
    
    for (const roomData of allRooms) {
      if (roomData.roomId !== currentRoomId) {
        const mergedRoomId = this.roomOutlineService.mergeAdjacentRooms(currentRoomId, roomData.roomId);
        if (mergedRoomId) {
          console.log(`[VirtualRoomPainterPlugin] Merged rooms: ${currentRoomId} and ${roomData.roomId}`);
          
          // Update the current room reference
          if (mergedRoomId === currentRoomId) {
            this.currentRoom = { ...this.currentRoom!, id: mergedRoomId };
          }
          
          break; // Only merge one pair at a time
        }
      }
    }
  }

  /**
   * Simple hash function for colors
   */
  private hashColor(color: string): number {
    let hash = 0;
    for (let i = 0; i < color.length; i++) {
      const char = color.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Update brush settings for virtual room painting
   */
  public updateBrushSettings(size: number, shape: 'square' | 'circle'): void {
    console.log('[VirtualRoomPainterPlugin] Updating brush settings:', { size, shape });
    this.setBrushSettings(size, shape);
  }

  /**
   * Set the current room for painting
   */
  public setCurrentRoom(room: VirtualRoom): void {
    console.log('[VirtualRoomPainterPlugin] Setting current room:', room);
    // Painted tiles are saved in batches per VR, and a closing page may only get one request out.
    // Saving on a VR switch keeps the unsaved tiles in at most one VR. Not awaited: switching
    // must not wait for Back4App, and not hurried: nothing is leaving, so a failed save pauses
    // between retries as an auto-save does.
    if (this.currentRoom?.id !== room.id) this.flushPendingTilesInBackground();
    this.currentRoom = room;
    
    // Update cursor color to match room color
    this.updateRoomColor(room.color);
    
    // Clear painted tiles for new room session
    this.paintedTiles.clear();
    console.log('[VirtualRoomPainterPlugin] Cleared painted tiles, now restoring from outline service...');
    
    // Restore any existing painted tiles for this room from the outline service
    this.restorePaintedTiles(room);
    
    console.log('[VirtualRoomPainterPlugin] After restoration, painted tiles count:', this.paintedTiles.size);
    
    // Show notification that room is ready for painting
    if (this.context.notificationService) {
      this.context.notificationService.show(`Ready to paint: ${room.name}`, 'info');
    }
  }

  private flushPendingTilesInBackground(): void {
    try {
      Promise.resolve(tileSyncManager.flush()).catch((error) => {
        console.error('[VirtualRoomPainterPlugin] Error saving pending tiles on VR switch:', error);
      });
    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Error saving pending tiles on VR switch:', error);
    }
  }

  /**
   * Restore painted tiles from room outline service
   */
  private restorePaintedTiles(room: VirtualRoom): void {
    const roomOutline = this.roomOutlineService.getRoomOutline(room.id);
    
    if (roomOutline && roomOutline.boundary && roomOutline.boundary.tiles) {
      // Restore painted tiles from the outline service
      const tiles = roomOutline.boundary.tiles;
      
      // Handle both Set and Array formats for backward compatibility
      if (tiles instanceof Set) {
        // Convert Set<string> to painted tiles
        const tileSet = tiles as Set<string>;
        tileSet.forEach((coord: string) => {
          this.paintedTiles.add(coord);
        });
        
        console.log('[VirtualRoomPainterPlugin] Restored', tileSet.size, 'painted tiles for room:', room.name);
      } else if (Array.isArray(tiles)) {
        // Handle array format (legacy)
        const tileArray = tiles as Array<any>;
        tileArray.forEach((tile: any) => {
          if (tile && typeof tile.x === 'number' && typeof tile.y === 'number') {
            this.paintedTiles.add(`${tile.x},${tile.y}`);
          }
        });
        
        console.log('[VirtualRoomPainterPlugin] Restored', tileArray.length, 'painted tiles for room:', room.name);
      }
      
      // Update the visual borders
      this.updateAdjacentTiles();
    }
  }

  /**
   * Disable edit mode while preserving painted tiles
   */
  public async disableEditMode(): Promise<void> {
    console.log('[VirtualRoomPainterPlugin] Disabling edit mode');
    
    if (!this.currentRoom) {
      console.warn('[VirtualRoomPainterPlugin] No current room to disable edit mode for');
      return;
    }

    // Store the current painted tiles in the room outline service before clearing
    console.log('[VirtualRoomPainterPlugin] Current painted tiles before saving:', this.paintedTiles.size);
    if (this.paintedTiles.size > 0) {
      const tileArray = Array.from(this.paintedTiles).map(coord => {
        const [x, y] = coord.split(',').map(Number);
        return { x, y };
      });

      console.log('[VirtualRoomPainterPlugin] Converting to tile array:', tileArray);

      // Update room outline with current painted tiles
      this.roomOutlineService.addRoomOutline(
        this.currentRoom.id,
        tileArray,
        this.currentRoom.color
      );

      // Save room outline data to storage
      await this.saveRoomOutlineToStorage(this.currentRoom.id, tileArray);

      console.log('[VirtualRoomPainterPlugin] Preserved', tileArray.length, 'painted tiles for room:', this.currentRoom.name);
    } else {
      console.log('[VirtualRoomPainterPlugin] No painted tiles to preserve for room:', this.currentRoom.name);
    }

    // Emit event for UI components to update (before clearing currentRoom)
    this.emit('editModeDisabled', { roomId: this.currentRoom.id });
    
    // Clear current room and painted tiles
    this.currentRoom = null;
    this.paintedTiles.clear();
    
    // Clear the border renderer (this will remove the edit mode borders)
    this.borderRenderer.clearBorders();
    
    // Render all preserved room borders
    this.renderPreservedRoomBorders();
    
    // Deactivate the plugin
    this.deactivate();
    
    // Show notification
    if (this.context.notificationService) {
      this.context.notificationService.show('Edit mode disabled - painted tiles preserved', 'info');
    }
  }

  /**
   * Check if a room is currently in edit mode
   */
  public isInEditMode(): boolean {
    return this.currentRoom !== null && this.isActive;
  }

  /**
   * Render all preserved room borders (for rooms not in edit mode)
   */
  public renderPreservedRoomBorders(): void {
    console.log('[VirtualRoomPainterPlugin] Rendering preserved room borders');
    
    const allRoomTiles = this.roomOutlineService.getAllRoomTiles();
    
    // Filter out the current room (if any) since it's handled separately in edit mode
    const preservedRooms = allRoomTiles.filter(roomData => 
      !this.currentRoom || roomData.roomId !== this.currentRoom.id
    );
    
    if (preservedRooms.length === 0) {
      console.log('[VirtualRoomPainterPlugin] No preserved rooms to render');
      return;
    }
    
    // Create a temporary room object for each preserved room and render its borders
    preservedRooms.forEach(roomData => {
      const tempRoom: VirtualRoom = {
        id: roomData.roomId,
        name: roomData.roomId, // Use roomId as name for preserved rooms
        color: roomData.color,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        isActive: false,
        mapId: currentMapId() ?? '',
        properties: {
          uniqueRoomId: roomData.roomId,
          proximityDistance: 5
        }
      };
      
      this.borderRenderer.paintRoomBorder(roomData.tiles, tempRoom);
    });
    
    console.log('[VirtualRoomPainterPlugin] Rendered borders for', preservedRooms.length, 'preserved rooms');
  }

  /**
   * Get the current room
   */
  public getCurrentRoom(): VirtualRoom | null {
    return this.currentRoom;
  }

  /**
   * Update cursor highlighting with room color
   */
  public updateRoomColor(color: string): void {
    console.log('[VirtualRoomPainterPlugin] Updating room color:', color);
    // Convert hex color to number for Phaser
    const colorNumber = parseInt(color.replace('#', ''), 16);
    this.setHighlightStyle(colorNumber, 0.9, 0.2);
    
    // Update border renderer color
    this.borderRenderer.updateOptions({ borderColor: color });
  }

  /**
   * Update room properties (name, color, uniqueRoomId, proximityDistance)
   */
  public updateRoomProperties(updates: Partial<VirtualRoom>): void {
    if (this.currentRoom) {
      this.currentRoom = { ...this.currentRoom, ...updates, updatedAt: Date.now() };
      console.log('[VirtualRoomPainterPlugin] Updated room properties:', updates);
      
      // Update room color if it changed
      if (updates.color) {
        this.updateRoomColor(updates.color);
      }
      
      // Emit event for UI components to update
      this.emit('roomPropertiesUpdated', { 
        roomId: this.currentRoom.id, 
        room: this.currentRoom 
      });
    }
  }

  /**
   * Get room outline service for external access
   */
  public getRoomOutlineService(): RoomOutlineService {
    return this.roomOutlineService;
  }

  /**
   * Get current room data for visualization
   */
  public getCurrentRoomData(): {
    room: VirtualRoom | null;
    paintedTiles: Array<{ x: number; y: number }>;
    tileCount: number;
    roomOutline: any;
  } {
    const tileArray = Array.from(this.paintedTiles).map(coord => {
      const [x, y] = coord.split(',').map(Number);
      return { x, y };
    });

    const roomOutline = this.currentRoom ? 
      this.roomOutlineService.getRoomOutline(this.currentRoom.id) : null;

    return {
      room: this.currentRoom,
      paintedTiles: tileArray,
      tileCount: this.paintedTiles.size,
      roomOutline: roomOutline
    };
  }

  /**
   * Get all room data for visualization
   */
  public getAllRoomData(): Array<{
    roomId: string;
    roomOutline: any;
    tileCount: number;
  }> {
    const allRooms = this.roomOutlineService.getAllRoomOutlines();
    return allRooms.map(roomData => ({
      roomId: roomData.roomId,
      roomOutline: roomData,
      tileCount: roomData.boundary.tiles.size,
    }));
  }

  /**
   * Clear all room outlines and tiles
   */
  public clearAllRoomOutlines(): void {
    this.roomOutlineService.clearAllOutlines();
    this.paintedTiles.clear();
    
    // Clear all room borders using the modular renderer
    this.borderRenderer.clearBorders();
    
    console.log('[VirtualRoomPainterPlugin] Cleared all room outlines and tiles');
  }

  /**
   * Export room outline data
   */
  public exportRoomOutlines(): Record<string, any> {
    return this.roomOutlineService.exportRoomOutlines();
  }

  /**
   * Import room outline data
   */
  public importRoomOutlines(data: Record<string, any>): void {
    this.roomOutlineService.importRoomOutlines(data);
  }

  /**
   * Test painting functionality - for debugging
   */
  public testPainting(x: number, y: number): void {
    console.log('[VirtualRoomPainterPlugin] Testing painting at:', { x, y });
    
    if (!this.currentRoom) {
      console.warn('[VirtualRoomPainterPlugin] No room selected for testing');
      return;
    }

    if (!this.context.mapService) {
      console.warn('[VirtualRoomPainterPlugin] MapService not available for testing');
      return;
    }

    try {
      // Use the addTileToRoom method which respects brush settings
      this.addTileToRoom(x, y);
      
      console.log('[VirtualRoomPainterPlugin] Test painting completed successfully');
      this.context.notificationService.show(`Test painted at (${x}, ${y}) with brush settings`, 'success');
      
    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Error in test painting:', error);
      this.context.notificationService.show('Test painting failed', 'error');
    }
  }

  /**
   * Debug plugin registration status
   */
  public debugRegistration(): void {
    console.log('[VirtualRoomPainterPlugin] Debug registration status:');
    console.log('- Plugin ID:', this.metadata.id);
    console.log('- Is Active:', this.isActive);
    console.log('- Is Initialized:', this.isInitialized);
    console.log('- Has MapPointers:', !!this.context.mapPointers);
    console.log('- Current Room:', this.currentRoom);
    console.log('- Painted Tiles Count:', this.paintedTiles.size);
    console.log('- Border Renderer Active:', this.borderRenderer.hasBorders());
    
    if (this.context.mapPointers) {
      console.log('- Registered Handlers:', Array.from(this.context.mapPointers['handlers']?.keys() || []));
    }
  }

  /**
   * Get current plugin state for UI components
   */
  public getPluginState(): {
    isActive: boolean;
    isPainting: boolean;
    brushSize: number;
    brushShape: 'square' | 'circle';
    lastPosition: { x: number; y: number } | null;
    editMode: 'add' | 'delete' | 'placePoint';
  } {
    const brushSettings = this.context.brushSettingsManager?.getSettings() || { size: 1, shape: 'square' };
    
    return {
      isActive: this.isActive,
      isPainting: this.isPainting,
      brushSize: brushSettings.size,
      brushShape: brushSettings.shape,
      lastPosition: this.lastPaintPosition,
      editMode: this.editMode
    };
  }

  /**
   * Plugin disposal - clean up resources
   */
  protected async onDispose(): Promise<void> {
    console.log('[VirtualRoomPainterPlugin] Disposing Virtual Room Painter plugin');
    
    // Clean up border renderer
    this.borderRenderer.destroy();
    
    // Reset state
    this.isPainting = false;
    this.lastPaintPosition = null;
    
    // Dispose database bridge
    try {
      databaseBridge.dispose();
      console.log('[VirtualRoomPainterPlugin] Database bridge disposed');
    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Error disposing database bridge:', error);
    }
    
    // Dispose tile sync manager. Painted tiles wait up to 30 s for their save, so send them first.
    let flushTimedOut = false;
    try {
      // Bounded: closing the editor must not hang on a Back4App that isn't answering.
      await Promise.race([
        tileSyncManager.flush({ hurry: true }),
        new Promise((resolve) => setTimeout(() => {
          flushTimedOut = true;
          resolve(undefined);
        }, MAP_EDITOR_FLUSH_TIMEOUT_MS)),
      ]);
    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Error saving pending tiles on dispose:', error);
    }
    // An editor opened while this one waited owns the manager now: its tiles are not ours to
    // report or clear, and its auto-save sends whatever this flush left behind.
    if (this.session !== latestSession) {
      console.log('[VirtualRoomPainterPlugin] A newer editor session owns the tile sync manager; leaving it');
      return;
    }
    // The manager is cleared below (its queue would otherwise write to whichever map is current
    // next), so say what the clear throws away: tiles still pending, failed or in flight.
    try {
      const unsaved = tileSyncManager.getUnsavedChanges().length;
      if (unsaved > 0) {
        // A save still running at the timeout may yet succeed after the editor is gone, so the
        // tiles are not known to be lost.
        const mayStillSave = flushTimedOut && tileSyncManager.getSyncStatus().isSyncing;
        const tiles = `${unsaved} painted ${unsaved === 1 ? 'tile' : 'tiles'}`;
        console.warn(`[VirtualRoomPainterPlugin] Closing with ${unsaved} painted tiles not saved`);
        this.context?.notificationService?.show(
          mayStillSave
            ? `${tiles} may not have been saved - the save was still running when the editor closed.`
            : `${tiles} ${unsaved === 1 ? 'was' : 'were'} not saved because the server did not answer or refused them.`,
          'error'
        );
      }
    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Error reporting unsaved tiles on dispose:', error);
    }
    try {
      tileSyncManager.dispose();
      console.log('[VirtualRoomPainterPlugin] Tile sync manager disposed');
    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Error disposing tile sync manager:', error);
    }
  }

  /**
   * Deletes a VR: Cloud Code first, in one call, then the editor's outline, tiles and borders.
   * Throws Cloud Code's refusal (e.g. upcoming bookings in the VR) and leaves the VR as it is.
   */
  public async deleteRoom(roomId: string): Promise<void> {
    await databaseBridge.deleteRoom(roomId);

    if (this.currentRoom && this.currentRoom.id === roomId) {
      await this.disableEditMode();
    }
    this.roomOutlineService.removeRoomOutline(roomId);
    this.borderRenderer.clearRoomBorder(roomId);
    tileSyncManager.clearPendingChangesForRoom(roomId);
    this.renderPreservedRoomBorders();
    this.emit('roomDeleted', { roomId });
  }

  /**
   * Set the current edit mode (add/delete tiles, or place a spawn point)
   */
  public setEditMode(mode: 'add' | 'delete' | 'placePoint'): void {
    this.editMode = mode;
    console.log('[VirtualRoomPainterPlugin] Edit mode set to:', mode);
    this.emit('editModeChanged', { mode });
  }

  /**
   * Get the current edit mode
   */
  public getEditMode(): 'add' | 'delete' | 'placePoint' {
    return this.editMode;
  }

  /** The editor room's map. */
  public getCurrentMapId(): string {
    return databaseBridge.getMapId() || currentMapId() || '';
  }

  /**
   * Enter 'placePoint' mode to place a VR's spawn point. The next map click supplies the tile
   * via handlePlacePointClick.
   */
  public beginPlacingSpawnPoint(virtualRoomId: string): void {
    this.pendingSpawnPointDraft = { tile: null, virtualRoomId };
    this.setEditMode('placePoint');
  }

  /**
   * Get a copy of the in-progress spawn point draft (tile is null until a map click stages it).
   */
  public getPendingSpawnPointDraft(): SpawnPointDraft | null {
    return this.pendingSpawnPointDraft ? { ...this.pendingSpawnPointDraft } : null;
  }

  /**
   * Cancel spawn point placement and return to 'add' mode without saving anything.
   */
  public cancelPlacingSpawnPoint(): void {
    this.pendingSpawnPointDraft = null;
    this.setEditMode('add');
    this.emit('spawnPointDraftCancelled', {});
  }

  /**
   * Validates the placed tile and saves it as the VR's `metadata.spawnPoint` (SC-422).
   *
   * A spawn point inside the VR is refused: arriving there would join the VR's audio and video
   * at once (see spawnPointValidation.ts). Distance from the VR only warns.
   */
  public async confirmSpawnPoint(): Promise<SpawnPointConfirmResult> {
    const draft = this.pendingSpawnPointDraft;
    if (!draft || !draft.tile) {
      return { success: false, reason: 'Click on the map to place the spawn point first.' };
    }

    const room =
      this.currentRoom?.id === draft.virtualRoomId ? this.currentRoom : await databaseBridge.getRoomById(draft.virtualRoomId);
    const validation = validateRoomArrivalPlacement(draft.tile, this.getRoomTilesById(draft.virtualRoomId), null, {
      allowInside: room?.kind === 'cubicle'
    });
    if (!validation.ok) {
      return { success: false, reason: validation.reason };
    }

    try {
      await databaseBridge.saveSpawnPoint(draft.virtualRoomId, draft.tile);
    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Failed to save spawn point:', error);
      return { success: false, reason: error instanceof Error ? error.message : 'Failed to save spawn point.' };
    }

    if (this.currentRoom && this.currentRoom.id === draft.virtualRoomId) {
      this.currentRoom = { ...this.currentRoom, metadata: { ...(this.currentRoom.metadata || {}), spawnPoint: draft.tile } };
    }
    this.pendingSpawnPointDraft = null;
    this.setEditMode('add');
    this.emit('spawnPointCreated', { virtualRoomId: draft.virtualRoomId, spawnPoint: draft.tile });
    return { success: true, warning: validation.warning };
  }

  /**
   * Get a room's painted tiles by its id, whether or not it's the room currently being edited —
   * used to validate a room-arrival placement against the target room's bounds.
   */
  private getRoomTilesById(roomId: string): Array<{ x: number; y: number }> {
    if (this.currentRoom && this.currentRoom.id === roomId) {
      return Array.from(this.paintedTiles).map(coord => {
        const [x, y] = coord.split(',').map(Number);
        return { x, y };
      });
    }

    const outline = this.roomOutlineService.getRoomOutline(roomId);
    if (!outline) return [];

    return Array.from(outline.boundary.tiles).map(coord => {
      const [x, y] = coord.split(',').map(Number);
      return { x, y };
    });
  }

  /**
   * Load room outlines from stored room data
   * This method should be called when rooms are loaded from storage
   */
  public async loadRoomOutlinesFromStoredData(rooms: VirtualRoom[]): Promise<void> {
    console.log('[VirtualRoomPainterPlugin] Loading room outlines from', rooms.length, 'stored rooms');
    
    // Clear existing outlines first
    this.roomOutlineService.clearAllOutlines();
    
    // Load outlines from each room's stored data (legacy outlineData)
    rooms.forEach(room => {
      if (room.outlineData && room.outlineData.tiles && room.outlineData.tiles.length > 0) {
        // Convert string coordinates back to tile objects
        const tiles = room.outlineData.tiles.map(coord => {
          const [x, y] = coord.split(',').map(Number);
          return { x, y };
        });
        
        // Add room outline to the service
        this.roomOutlineService.addRoomOutline(room.id, tiles, room.color);
        console.log('[VirtualRoomPainterPlugin] Loaded legacy outline for room:', room.name, 'with', tiles.length, 'tiles');
      } else {
        console.log('[VirtualRoomPainterPlugin] No legacy outline data for room:', room.name);
      }
    });
    
    // Load tiles from database for each room and merge with stored data
    // Note: This will use cached data if available from plugin initialization
    try {
      const storedTiles = await tileSyncManager.loadTilesForMap(requireCurrentMapId());
      // Tiles painted within the last 30 s are not stored yet; without them a VR switch would
      // redraw those VRs without their newest tiles.
      const databaseTiles = overlayUnsavedTiles(storedTiles, tileSyncManager.getUnsavedChanges());
      
      if (databaseTiles.length > 0) {
        console.log('[VirtualRoomPainterPlugin] Found', databaseTiles.length, 'tiles in database and unsaved (may be cached)');
        
        // Group tiles by roomId
        const tilesByRoom: Map<string, Array<{ tileX: number; tileY: number; tileType: 'room' | 'outline' }>> = new Map();
        
        databaseTiles.forEach(tile => {
          if (!tilesByRoom.has(tile.roomId)) {
            tilesByRoom.set(tile.roomId, []);
          }
          tilesByRoom.get(tile.roomId)!.push({
            tileX: tile.tileX,
            tileY: tile.tileY,
            tileType: tile.tileType
          });
        });

        // Process each room's database tiles
        for (const [roomId, roomTiles] of tilesByRoom) {
          // Separate room tiles from outline tiles
          const roomTileCoords = roomTiles
            .filter(tile => tile.tileType === 'room')
            .map(tile => ({ x: tile.tileX, y: tile.tileY }));

          if (roomTileCoords.length > 0) {
            // Find the room to get its color
            const room = rooms.find(r => r.id === roomId);
            const roomColor = room?.color || '#FF0000';
            
            // Add or update room outline with database tiles
            this.roomOutlineService.addRoomOutline(roomId, roomTileCoords, roomColor);
            console.log('[VirtualRoomPainterPlugin] Loaded database tiles for room:', roomId, 'with', roomTileCoords.length, 'tiles');
          }
        }
      }
    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Failed to load tiles from database during room data load:', error);
    }
    
    // Render the loaded room borders
    this.renderPreservedRoomBorders();
  }

  /**
   * Load the map's tiles from the database for all its rooms.
   * Called when the plugin initializes.
   */
  public async loadTilesFromDatabase(): Promise<void> {
    console.log('[VirtualRoomPainterPlugin] Loading tiles from database for map:', this.getCurrentMapId());
    
    try {
      const tiles = await tileSyncManager.loadTilesForMap(requireCurrentMapId());
      
      if (tiles.length === 0) {
        console.log('[VirtualRoomPainterPlugin] No tiles found in database for map:', this.getCurrentMapId());
        return;
      }

      console.log('[VirtualRoomPainterPlugin] Loaded', tiles.length, 'tiles from database');

      // Group tiles by roomId
      const tilesByRoom: Map<string, Array<{ tileX: number; tileY: number; tileType: 'room' | 'outline' }>> = new Map();
      
      tiles.forEach(tile => {
        if (!tilesByRoom.has(tile.roomId)) {
          tilesByRoom.set(tile.roomId, []);
        }
        tilesByRoom.get(tile.roomId)!.push({
          tileX: tile.tileX,
          tileY: tile.tileY,
          tileType: tile.tileType
        });
      });

      // Process each room's tiles
      for (const [roomId, roomTiles] of tilesByRoom) {
        // Separate room tiles from outline tiles
        const roomTileCoords = roomTiles
          .filter(tile => tile.tileType === 'room')
          .map(tile => ({ x: tile.tileX, y: tile.tileY }));

        const outlineTileCoords = roomTiles
          .filter(tile => tile.tileType === 'outline')
          .map(tile => ({ x: tile.tileX, y: tile.tileY }));

        if (roomTileCoords.length > 0) {
          // Add room outline to the service
          this.roomOutlineService.addRoomOutline(roomId, roomTileCoords, '#FF0000'); // Default color, will be updated when room data loads
          console.log('[VirtualRoomPainterPlugin] Loaded', roomTileCoords.length, 'room tiles for room:', roomId);
        }

        if (outlineTileCoords.length > 0) {
          console.log('[VirtualRoomPainterPlugin] Loaded', outlineTileCoords.length, 'outline tiles for room:', roomId);
        }
      }

      // Render the loaded room borders
      this.renderPreservedRoomBorders();
      
      console.log('[VirtualRoomPainterPlugin] Successfully loaded tiles from database for', tilesByRoom.size, 'rooms');

    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Failed to load tiles from database:', error);
    }
  }

  /**
   * Load tiles from database for a specific room
   * This method can be called when a specific room is selected
   */
  public async loadTilesForRoom(roomId: string): Promise<void> {
    console.log('[VirtualRoomPainterPlugin] Loading tiles from database for room:', roomId);
    
    try {
      const tiles = await tileSyncManager.loadTilesForRoom(roomId);
      
      if (tiles.length === 0) {
        console.log('[VirtualRoomPainterPlugin] No tiles found in database for room:', roomId);
        return;
      }

      console.log('[VirtualRoomPainterPlugin] Loaded', tiles.length, 'tiles from database for room:', roomId);

      // Separate room tiles from outline tiles
      const roomTileCoords = tiles
        .filter(tile => tile.tileType === 'room')
        .map(tile => ({ x: tile.tileX, y: tile.tileY }));

      const outlineTileCoords = tiles
        .filter(tile => tile.tileType === 'outline')
        .map(tile => ({ x: tile.tileX, y: tile.tileY }));

      if (roomTileCoords.length > 0) {
        // Add room outline to the service
        this.roomOutlineService.addRoomOutline(roomId, roomTileCoords, '#FF0000'); // Default color, will be updated when room data loads
        console.log('[VirtualRoomPainterPlugin] Loaded', roomTileCoords.length, 'room tiles for room:', roomId);
      }

      if (outlineTileCoords.length > 0) {
        console.log('[VirtualRoomPainterPlugin] Loaded', outlineTileCoords.length, 'outline tiles for room:', roomId);
      }

      // Update visual borders for this room
      // Note: We'll let the renderPreservedRoomBorders handle this since we don't have the room object here

    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Failed to load tiles for room from database:', error);
    }
  }

  /**
   * Save room outline data to storage
   * This method should be called when a room's outline changes
   */
  public async saveRoomOutlineToStorage(roomId: string, tiles: Array<{ x: number; y: number }>): Promise<void> {
    try {
      // Safety check: don't save data if the room is being deleted
      if (!this.currentRoom || this.currentRoom.id !== roomId) {
        console.log('[VirtualRoomPainterPlugin] Skipping outline save - room not in edit mode or being deleted:', roomId);
        return;
      }
      
      // Convert tiles to string coordinates for storage
      const tileCoordinates = tiles.map(tile => `${tile.x},${tile.y}`);
      
      // Get the room outline to extract outline tiles
      const roomOutline = this.roomOutlineService.getRoomOutline(roomId);
      const outlineCoordinates = roomOutline ? Array.from(roomOutline.boundary.outlineTiles) : [];
      
      // Prepare outline data for storage
      const outlineData = {
        tiles: tileCoordinates,
        outlineTiles: outlineCoordinates,
        lastUpdated: Date.now()
      };
      
      // Update room in local storage with outline data
      await databaseBridge.updateRoom(roomId, { 
        outlineData,
        updatedAt: Date.now()
      });
      
      // Emit event for the sidebar to update the room in storage
      this.emit('roomOutlineUpdated', { 
        roomId, 
        outlineData 
      });
      
      console.log('[VirtualRoomPainterPlugin] Saved outline data for room:', roomId, 'with', tiles.length, 'tiles');
    } catch (error) {
      console.error('[VirtualRoomPainterPlugin] Failed to save room outline to storage:', error);
    }
  }
}
